import {useState} from 'react';
import {Card, Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import LoadingOverlay from '../../../../app/common/components/LoadingOverlay';
import {
  useAddPublicRule,
  useGetFirewallPublic,
  useMovePublicRule,
  useRemovePublicRule,
} from '../../Hooks/RouterManager';
import AddPublicRuleModal from './AddPublicRuleModal';
import ActionIcon from '../../../../Components/ActionIcon';

interface FirewallPublicPageProps {
  routerId: string;
}

const FirewallPublicPage = ({routerId}: FirewallPublicPageProps): JSX.Element => {
  const intl = useIntl();
  const {data, isLoading, isFetching} = useGetFirewallPublic(routerId);
  const [showAdd, setShowAdd] = useState(false);
  const addRule = useAddPublicRule(routerId);
  const removeRule = useRemovePublicRule(routerId);
  const moveRule = useMovePublicRule(routerId);

  const moveUp = (id: string) => moveRule.mutate(id);

  const handleDelete = (id: string) => {
    if (window.confirm(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.CONFIRM_DELETE'}))) {
      removeRule.mutate(id);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="text-center py-10">
        <Spinner animation="border" />
      </div>
    );
  }

  const rules = data.rules;

  return (
    <LoadingOverlay isLoading={isFetching}>
      <Card>
        <Card.Header>
          <Card.Title>
            <Level3Title
              title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC.TITLE'})}
            />
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-6">
            {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC.SUBTITLE'})}
          </p>
          <table className="table table-row-dashed align-middle">
            <thead>
              <tr className="fw-semibold text-muted">
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PROTOCOL'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.SOURCE_IP'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.SOURCE_PORT'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.DEST_IP'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.DEST_PORT'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ACTION'})}</th>
                <th style={{width: 90}}></th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted text-center">
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.EMPTY'})}
                  </td>
                </tr>
              )}
              {rules.map((r, idx) => (
                <tr key={r.id}>
                  <td>{r.protocol.toUpperCase()}</td>
                  <td>
                    {r.sourceIp || (
                      <span className="text-muted">
                        {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ALL'})}
                      </span>
                    )}
                  </td>
                  <td>
                    {r.sourcePort || (
                      <span className="text-muted">
                        {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ALL'})}
                      </span>
                    )}
                  </td>
                  <td>{r.destinationIp}</td>
                  <td>
                    {r.destinationPort || (
                      <span className="text-muted">
                        {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ALL'})}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`badge bg-${r.action === 'accept' ? 'success' : 'danger'}`}>
                      {intl.formatMessage({
                        id:
                          r.action === 'accept'
                            ? 'CONFIGURATION.ROUTEUR.FIREWALL.ACCEPT'
                            : 'CONFIGURATION.ROUTEUR.FIREWALL.DROP',
                      })}
                    </span>
                  </td>
                  <td>
                    {idx > 0 && (
                      <ActionIcon
                        label={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.MOVE_UP'})}
                        faIcon="fa-solid fa-arrow-up text-primary"
                        onClick={() => moveUp(r.id)}
                      />
                    )}
                    <ActionIcon
                      label={intl.formatMessage({id: 'COMMON.DELETE'})}
                      faIcon="fa-solid fa-trash text-danger"
                      onClick={() => handleDelete(r.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-light btn-sm" onClick={() => setShowAdd(true)}>
            <i className="fa-solid fa-plus me-2" />
            {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ADD_RULE'})}
          </button>
          <AddPublicRuleModal
            show={showAdd}
            onHide={() => setShowAdd(false)}
            onAdd={(r) =>
              addRule.mutate({
                protocol: r.protocol,
                sourceIp: r.sourceIp,
                sourcePort: r.sourcePort,
                destinationIp: r.destinationIp,
                destinationPort: r.destinationPort,
                action: r.action,
              })
            }
            destinations={data.destinations}
          />
        </Card.Body>
      </Card>
    </LoadingOverlay>
  );
};

export default FirewallPublicPage;
